import { useMemo, useState, type ComponentType } from 'react';
import {
  ActionIcon,
  Button,
  Group,
  Paper,
  SegmentedControl,
  Spoiler,
  Stack,
  Text,
  Textarea,
  ThemeIcon,
  Timeline,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import dayjs from 'dayjs';
import {
  PiChatCircleText,
  PiFlagBanner,
  PiInfo,
  PiPencilSimpleLine,
  PiSortAscending,
  PiSortDescending,
} from 'react-icons/pi';

export type FeedChange = {
  label: string;
  from: string;
  to: string;
};

export type FeedItem = {
  id: string;
  at: string;
  actor: string | null;
  kind: 'update' | 'comment' | 'status' | 'system';
  title: string;
  changes?: FeedChange[];
  body?: string;
};

const TYPE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'update', label: 'Updates' },
  { value: 'comment', label: 'Comments' },
  { value: 'status', label: 'Status' },
  { value: 'system', label: 'System' },
];

const KIND_BULLETS: Record<
  FeedItem['kind'],
  { icon: ComponentType<{ size?: number }>; color: string }
> = {
  update: { icon: PiPencilSimpleLine, color: 'blue' },
  comment: { icon: PiChatCircleText, color: 'teal' },
  status: { icon: PiFlagBanner, color: 'orange' },
  system: { icon: PiInfo, color: 'gray' },
};

type Props = {
  items: FeedItem[];
  onPostComment?: (body: string) => Promise<unknown>;
  posting?: boolean;
};

export function ActivityFeed({ items, onPostComment, posting }: Props) {
  const [type, setType] = useState('all');
  const [range, setRange] = useState<[string | null, string | null]>([
    null,
    null,
  ]);
  const [desc, setDesc] = useState(true);
  const [body, setBody] = useState('');

  const monthGroups = useMemo(() => {
    let list = items;
    if (type !== 'all') list = list.filter((i) => i.kind === type);
    const [from, to] = range;
    if (from)
      list = list.filter((i) => dayjs(i.at).format('YYYY-MM-DD') >= from);
    if (to) list = list.filter((i) => dayjs(i.at).format('YYYY-MM-DD') <= to);
    const sorted = [...list].sort((a, b) =>
      desc ? b.at.localeCompare(a.at) : a.at.localeCompare(b.at),
    );
    const groups: { month: string; items: FeedItem[] }[] = [];
    for (const item of sorted) {
      const month = dayjs(item.at).format('MMMM YYYY');
      const last = groups[groups.length - 1];
      if (last && last.month === month) {
        last.items.push(item);
      } else {
        groups.push({ month, items: [item] });
      }
    }
    return groups;
  }, [items, type, range, desc]);

  return (
    <Stack gap="md">
      <Paper withBorder p="sm">
        <Group gap="sm">
          <SegmentedControl
            size="xs"
            data={TYPE_OPTIONS}
            value={type}
            onChange={setType}
          />
          <DatePickerInput
            type="range"
            size="xs"
            w={230}
            placeholder="Date range"
            clearable
            allowSingleDateInRange
            value={range}
            onChange={(v) => setRange(v as [string | null, string | null])}
          />
          <ActionIcon
            variant="default"
            size="input-xs"
            onClick={() => setDesc((d) => !d)}
            title={desc ? 'Newest first' : 'Oldest first'}
          >
            {desc ? (
              <PiSortDescending size={16} />
            ) : (
              <PiSortAscending size={16} />
            )}
          </ActionIcon>
        </Group>
      </Paper>

      {onPostComment && (
        <Paper withBorder p="sm">
          <Textarea
            placeholder="Add a comment..."
            value={body}
            onChange={(e) => setBody(e.currentTarget.value)}
            autosize
            minRows={2}
          />
          <Group justify="flex-end" mt="xs">
            <Button
              size="compact-sm"
              disabled={!body.trim()}
              loading={posting}
              onClick={() =>
                onPostComment(body).then(
                  () => setBody(''),
                  () => undefined,
                )
              }
            >
              Post
            </Button>
          </Group>
        </Paper>
      )}

      {monthGroups.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="md">
          No activity to show.
        </Text>
      ) : (
        monthGroups.map((group) => (
          <div key={group.month}>
            <Text size="sm" fw={600} mb="sm">
              {group.month}
            </Text>
            <Timeline bulletSize={26} lineWidth={2} color="gray">
              {group.items.map((item) => {
                const bullet = KIND_BULLETS[item.kind];
                return (
                  <Timeline.Item
                    key={item.id}
                    bullet={
                      <ThemeIcon
                        size={26}
                        radius="xl"
                        variant="light"
                        color={bullet.color}
                      >
                        <bullet.icon size={14} />
                      </ThemeIcon>
                    }
                  >
                    <FeedEntry item={item} />
                  </Timeline.Item>
                );
              })}
            </Timeline>
          </div>
        ))
      )}
    </Stack>
  );
}

function FeedEntry({ item }: { item: FeedItem }) {
  return (
    <Paper withBorder p="sm">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
          <Text fw={500} size="sm">
            {item.title}
          </Text>
          {item.changes?.map((c, i) => (
            <Text key={i} size="sm" style={{ wordBreak: 'break-word' }}>
              <Text span c="dimmed">
                {c.label}:
              </Text>{' '}
              {c.from} → {c.to}
            </Text>
          ))}
          {item.body && (
            <Spoiler maxHeight={60} showLabel="Show more" hideLabel="Show less">
              <Text
                size="sm"
                style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
              >
                {item.body}
              </Text>
            </Spoiler>
          )}
        </Stack>
        <Text size="xs" c="dimmed" ta="right" style={{ whiteSpace: 'nowrap' }}>
          {dayjs(item.at).format('DD.MM.YYYY HH:mm')}
          {item.actor ? `, ${item.actor}` : ''}
        </Text>
      </Group>
    </Paper>
  );
}
